import { Injectable } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { UsersService } from '@treasury/domain/backoffice/services';
import { CompanyUserSummary } from '@treasury/domain/company/user/entities';
import { FiUser } from '@treasury/domain/users/fi-user.entity';
import {
    SideDrawerButton,
    SideDrawerButtonPosture,
    TmSideDrawerService,
} from '@treasury/presentation/services/side-drawer';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { html } from 'lit';
import { RowActionCommand, RowActionCommandResult } from '../../../base.cmd';
import { ResetPasswordType } from '../overlays/drawers/reset-password/reset-password.component';
import { SharedUsersActionType } from '../users-actions.types';

export type ResetPasswordDrawerData = {
    temporaryPassword?: string;
    passwordType?: ResetPasswordType;
};
@Injectable()
export class LegacyResetPasswordCommand<
    T extends CompanyUserSummary | FiUser,
> implements RowActionCommand<T, SharedUsersActionType> {
    constructor(
        private readonly sideDrawerService: TmSideDrawerService,
        private readonly usersService: UsersService,
        private readonly toastService: TmToastService
    ) {}

    private changePasswordType: ResetPasswordType = ResetPasswordType.Email;

    private temporaryPassword: string = '';

    public command(
        isUisEnabled: boolean,
        canResetUserPassword: boolean
    ): RowActionCommandResult<T, SharedUsersActionType> {
        return {
            type: SharedUsersActionType.LegacyResetPassword,
            title: 'Reset Password',
            hiddenCondition: !isUisEnabled,
            disabledCondition: !canResetUserPassword,
            action: (user: T) => this.execute(user, canResetUserPassword),
        };
    }

    private async execute(user: T, canResetUserPassword: boolean) {
        if (!canResetUserPassword) {
            throw new Error(
                "The current user does not have the proper permissions to reset the user's password"
            );
        }

        const buttons: SideDrawerButton<ResetPasswordDrawerData>[] = [
            {
                label: 'Cancel',
                reason: DynamicComponentExitReason.Cancel,
                posture: SideDrawerButtonPosture.Tertiary,
            },
            {
                label: 'Confirm',
                reason: DynamicComponentExitReason.Confirm,
                posture: SideDrawerButtonPosture.Primary,
                checkEnabled: data => {
                    return (
                        data?.passwordType !== ResetPasswordType.Manual ||
                        (data?.passwordType === ResetPasswordType.Manual &&
                            (data?.temporaryPassword ?? '')) !== ''
                    );
                },
                onClick: close => {
                    this.handlePasswordChange(user?.id);
                    close();
                },
            },
        ];

        await this.sideDrawerService.open<ResetPasswordDrawerData>(
            html`<tm-bo-reset-password
                .user=${user}
                @change-password-type=${(e: CustomEvent) => {
                    this.changePasswordType = e.detail;
                }}
                @temporary-password-change=${(e: CustomEvent) => {
                    this.temporaryPassword = e.detail;
                }}
            >
            </tm-bo-reset-password>`,
            'Reset User Password',
            {
                buttons: buttons as SideDrawerButton<unknown>[],
            }
        );
    }

    private async handlePasswordChange(userId: number | null | undefined) {
        const { changePasswordType, temporaryPassword } = this;
        if (!userId) {
            this.toastService.show(
                'An error occurred while resetting the user password',
                TmToastPosture.Danger
            );
            throw new Error('User ID is required');
        }
        // const formModel = e.detail as ResetPasswordForm;
        if (changePasswordType === ResetPasswordType.Manual) {
            if (!temporaryPassword) {
                this.toastService.show(
                    'An error occurred while manually resetting the user password',
                    TmToastPosture.Danger
                );
                throw new Error('Temporary password is required');
            }
            this.manualResetPassword(userId, temporaryPassword);
        } else {
            this.sendResetEmail(userId);
        }
        this.sideDrawerService.close();
    }

    private async manualResetPassword(userId: number, password: string) {
        try {
            await this.usersService.manualResetPassword(userId, password);
            this.toastService.show("User's password reset successfully", TmToastPosture.Success);
        } catch {
            this.toastService.show(
                'An error occurred while resetting the user password',
                TmToastPosture.Danger
            );
        }
    }

    private async sendResetEmail(userId: number) {
        try {
            await this.usersService.resetPassword(userId);
            this.toastService.show('Reset password email sent', TmToastPosture.Success);
        } catch {
            this.toastService.show(
                'An error occurred while sending the reset password email',
                TmToastPosture.Danger
            );
        }
    }
}
